import { get } from 'lodash'
import {
  type GetStaticPaths,
  type GetStaticProps,
  type NextPageWithLayout,
} from 'next'
import React from 'react'
import { prepareGetStaticProps } from '@public/CmsPage/prepareGetStaticProps'
import { getPublicLayout } from '@public/components/PublicLayout'
import {
  COLLECTION_WIDGET_LIMIT,
  SHOWROOM_BRANDS_PAGE_SLUG,
} from 'constants/common'
import {
  CmsBrandPageBySlugDocument,
  type CmsWidget,
  ProductSorterField,
  ProductStatus,
  ProductVariantStatus,
  SortDirection,
  useCmsBrandPageBySlugQuery,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { ShowroomPageView } from './ShowroomPageView'

interface BrandPageProps {
  slug: string
}

export const BrandPage: NextPageWithLayout<BrandPageProps> = ({ slug }) => {
  const { MOBILE: isMobile } = useMedia()
  const { data, loading } = useCmsBrandPageBySlugQuery({
    fetchPolicy: 'cache-first',
    variables: {
      slug,
      widgetsDefaultInputs: {
        cmsMultiCollectionProductsInput: {
          pagination: {
            page: 1,
            limit: isMobile
              ? COLLECTION_WIDGET_LIMIT.MOBILE
              : COLLECTION_WIDGET_LIMIT.DESKTOP,
          },
          sorter: {
            field: ProductSorterField.PositionIndexWeight,
            direction: SortDirection.Asc,
          },
          filter: {
            statuses: [ProductStatus.Published],
            variantStatuses: [ProductVariantStatus.Published],
          },
        },
      },
    },
    skip: !slug,
  })

  const pageBySlug = get(data, ['cmsBrandPageBySlug']) as any
  const widgets: CmsWidget[] = get(pageBySlug, ['cmsPage', 'widgets'])

  return (
    <ShowroomPageView
      slug={slug}
      data={pageBySlug}
      isLoading={loading}
      widgets={widgets}
      isSearchFilterParamsMode
      {...(pageBySlug && {
        fallbackMetaTitle: `Shop ${pageBySlug.cmsPage.title}`,
        fallbackDescription: `Shop exclusive ${pageBySlug.cmsPage.title} pieces, curated by the world's top interior designers.`,
      })}
    />
  )
}

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps: GetStaticProps = (context) => {
  const brandSlug = get(context, ['params', 'slug'])
  const slug = `${SHOWROOM_BRANDS_PAGE_SLUG}/${brandSlug}`
  return prepareGetStaticProps(context, {
    slug,
    query: CmsBrandPageBySlugDocument,
  })
}

BrandPage.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true, isFullWidth: true })
